import React, { forwardRef, memo, useState, useEffect, useRef } from 'react';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import CollapsedSlot from './CollapsedSlot';
import SwimlaneRow from './SwimlaneRow';
import SwimlaneStickyHeader from './SwimlaneStickyHeader';

/**
 * SwimlanesBoard - Swimlanes mode with grouped rows
 */
const SwimlanesBoard = memo(forwardRef(({
  columns,
  columnIds,
  tasksByStatus,
  swimlanes,
  sortSetting,
  collapsedColumns,
  expandingColumns,
  selectedTaskIds,
  activeColumnId,
  activeCardId,
  activeCardSwimlaneId, // Swimlane ID of the card being dragged (for performance optimization)
  currentSwimlaneId,
  swimlaneRowRefs,
  swimlaneSetting,
  statuses,
  cardHandlerProps,
  editingCardId,
  setEditingCardId,
  onToggleCollapse,
  onDeleteColumn,
  onAddStatus,
  onReassignTasksStatus,
  onAddTask,
  onAddDecision,
  onAddWait,
  onRenameStatus,
  onBoardClick,
}, ref) => {
  // Track the index of the last visible swimlane when card drag starts
  // Swimlanes below this can have ALL cards hidden (more aggressive optimization)
  const [lastVisibleSwimlaneIndex, setLastVisibleSwimlaneIndex] = useState(null);
  const prevActiveCardSwimlaneIdRef = useRef(null);

  // Calculate last visible swimlane index when card drag starts
  useEffect(() => {
    if (activeCardSwimlaneId && !prevActiveCardSwimlaneIdRef.current) {
      // Card drag just started - calculate which swimlanes are visible
      const containerRect = ref?.current?.getBoundingClientRect();
      if (containerRect) {
        let lastVisibleIdx = -1;
        
        swimlanes.forEach((swimlane, index) => {
          const rowEl = swimlaneRowRefs.current[swimlane.id];
          if (rowEl) {
            const rowRect = rowEl.getBoundingClientRect();
            // Consider a swimlane "visible" if any part of it is in the viewport
            const isVisible = rowRect.top < containerRect.bottom && rowRect.bottom > containerRect.top;
            if (isVisible) {
              lastVisibleIdx = index;
            }
          }
        });
        
        setLastVisibleSwimlaneIndex(lastVisibleIdx);
      }
    } else if (!activeCardSwimlaneId && prevActiveCardSwimlaneIdRef.current) {
      // Card drag ended - clear the frozen index
      setLastVisibleSwimlaneIndex(null);
    }
    
    prevActiveCardSwimlaneIdRef.current = activeCardSwimlaneId;
  }, [activeCardSwimlaneId, swimlanes, swimlaneRowRefs, ref]);

  const boardClasses = [
    'kanban-swimlanes',
    activeColumnId && 'kanban-board--column-dragging',
    activeCardId && 'kanban-board--card-dragging',
  ].filter(Boolean).join(' ');

  return (
    <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
      <div ref={ref} className={boardClasses} onClick={onBoardClick}>
        <div className="kanban-swimlanes__content">
          {/* Collapsed Column Strips */}
          <div className="kanban-swimlanes__collapsed-strips">
            <div className="kanban-swimlanes__collapsed-strips-inner">
              <div className="kanban-swimlanes__header-spacer" />
              {columns.map((column, index) => (
                <CollapsedSlot
                  key={column.id}
                  columnId={column.id}
                  columnIndex={index}
                  isCollapsed={collapsedColumns.has(column.id)}
                  columnCount={(tasksByStatus[column.id] || []).length}
                  column={column}
                  onToggleCollapse={onToggleCollapse}
                  activeColumnId={activeColumnId}
                />
              ))}
            </div>
          </div>

          {/* Column Headers */}
          <SwimlaneStickyHeader
            columns={columns}
            collapsedColumns={collapsedColumns}
            tasksByStatus={tasksByStatus}
            statuses={statuses}
            swimlanes={swimlanes}
            currentSwimlaneId={currentSwimlaneId}
            activeColumnId={activeColumnId}
            onToggleCollapse={onToggleCollapse}
            onDeleteColumn={onDeleteColumn}
            onAddStatus={onAddStatus}
            onReassignTasksStatus={onReassignTasksStatus}
            onAddTask={onAddTask}
            onAddDecision={onAddDecision}
            onAddWait={onAddWait}
            onSetEditingCardId={setEditingCardId}
            onRenameStatus={onRenameStatus}
          />

          {/* Swimlane Rows */}
          {swimlanes.map((swimlane, swimlaneIndex) => {
            const swimlaneTasksByStatus = {};
            columns.forEach(col => {
              const tasksInColumn = swimlane.tasks.filter(t => t.status === col.id);
              swimlaneTasksByStatus[col.id] = sortSetting === 'manual'
                ? tasksInColumn.sort((a, b) => (a.kanbanOrder ?? Infinity) - (b.kanbanOrder ?? Infinity))
                : tasksInColumn;
            });

            // Performance optimization: when a card is being dragged in a different swimlane,
            // hide non-visible cards in this swimlane to reduce rendering overhead
            const isCardDraggingInOtherSwimlane = activeCardSwimlaneId && activeCardSwimlaneId !== swimlane.id;
            
            // More aggressive optimization: swimlanes BELOW the last visible one can hide ALL cards
            // since they don't affect the current scroll position or layout
            const isBelowVisibleArea = activeCardSwimlaneId && 
              lastVisibleSwimlaneIndex !== null && 
              swimlaneIndex > lastVisibleSwimlaneIndex;

            return (
              <SwimlaneRow
                key={swimlane.id}
                ref={(el) => { swimlaneRowRefs.current[swimlane.id] = el; }}
                swimlane={swimlane}
                columns={columns}
                collapsedColumns={collapsedColumns}
                expandingColumns={expandingColumns}
                tasksByStatus={swimlaneTasksByStatus}
                swimlaneSetting={swimlaneSetting}
                selectedTaskIds={selectedTaskIds}
                activeCardId={activeCardId}
                activeColumnId={activeColumnId}
                isAnyColumnDragging={!!activeColumnId}
                isAnyCardDragging={!!activeCardId}
                isCardDraggingInOtherSwimlane={isCardDraggingInOtherSwimlane}
                isBelowVisibleArea={isBelowVisibleArea}
                {...cardHandlerProps}
              />
            );
          })}
        </div>
      </div>
    </SortableContext>
  );
}));

export default SwimlanesBoard;

